import { FunctionalComponent } from 'preact'
import { useState, useRef, useEffect, useCallback } from 'preact/hooks'
import { Phone as PhoneIcon, Loader2, Info } from 'lucide-preact'
import { leadsApi, type PhoneEnrichmentResultDto } from '@/api/leads'
import { audienceApi } from '@/api/audience'
import { useToast } from '@/hooks/useToast'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'

interface PhoneEnrichmentCellProps {
  phone?: string | null
  globalContactPhone?: string | null
  phoneEnrichmentAvailableDate?: string | null
  /** ID of the record - either leadId (when context='lead') or fileImportRowId (when context='fileImportRow') */
  id: string
  workspaceId: string
  campaignId: string
  clientCompanyId: string
  onPhoneUpdated?: (updated: { Phone?: string | null; GlobalContactPhone?: string | null; PhoneEnrichmentAvailableDate?: string | null }) => void
  isLastRow?: boolean
  /** Context determines which API to call: 'lead' uses leads API, 'fileImportRow' uses audience API */
  context?: 'lead' | 'fileImportRow'
  /** Optional override. When omitted, edit capability is derived from the current workspace. When false, hides the enrich and update actions (display-only mode). */
  canEdit?: boolean
}

/**
 * Reusable component for displaying phone with enrichment and update capabilities.
 * Shows phone number, enrich button (if available), and update indicator (if new phone available).
 */
export const PhoneEnrichmentCell: FunctionalComponent<PhoneEnrichmentCellProps> = ({
  phone,
  globalContactPhone,
  phoneEnrichmentAvailableDate,
  id,
  clientCompanyId,
  workspaceId,
  campaignId,
  onPhoneUpdated,
  isLastRow = false,
  context = 'lead',
  canEdit: canEditProp
}) => {
  const [isEnriching, setIsEnriching] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<'bottom' | 'top'>(isLastRow ? 'top' : 'bottom')
  const enrichButtonRef = useRef<HTMLButtonElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const { showSuccess, showError, showInfo } = useToast()
  // canEdit may be passed explicitly; otherwise derive it from the current workspace.
  const { canEdit: workspaceCanEdit } = useWorkspaceCapabilities(clientCompanyId, workspaceId)
  const canEdit = canEditProp ?? workspaceCanEdit

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  // Update tooltip position based on isLastRow prop
  useEffect(() => {
    setTooltipPosition(isLastRow ? 'top' : 'bottom')
  }, [isLastRow])

  // Detect if tooltip would overflow viewport and adjust position
  const checkTooltipPosition = useCallback(() => {
    if (!enrichButtonRef.current) return

    // If it's the last row, always show above
    if (isLastRow) {
      setTooltipPosition('top')
      return
    }

    const rect = enrichButtonRef.current.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const tooltipHeight = 120 // Approximate height of tooltip with padding
    const spaceBelow = viewportHeight - rect.bottom

    // If not enough space below, show tooltip above
    setTooltipPosition(spaceBelow < tooltipHeight ? 'top' : 'bottom')
  }, [isLastRow])

  // Check position on hover
  useEffect(() => {
    const button = enrichButtonRef.current
    if (!button) return

    const handleMouseEnter = () => checkTooltipPosition()
    button.addEventListener('mouseenter', handleMouseEnter)

    return () => {
      button.removeEventListener('mouseenter', handleMouseEnter)
    }
  }, [checkTooltipPosition])

  const canEnrich = !phoneEnrichmentAvailableDate || new Date(phoneEnrichmentAvailableDate) <= new Date()
  const hasUpdatedPhone = globalContactPhone && globalContactPhone !== phone

  const handleEnrichPhone = async (e: MouseEvent) => {
    e.stopPropagation()
    if (!canEnrich || isEnriching) return

    abortControllerRef.current?.abort()
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsEnriching(true)
    try {
      const resp = context === 'fileImportRow'
            ? await audienceApi.enrichPhone(clientCompanyId, workspaceId, campaignId, id, abortController.signal)
        : await leadsApi.enrichPhone(workspaceId, campaignId, id, abortController.signal)
      const updated = resp?.data as PhoneEnrichmentResultDto | undefined
      if (updated) {
        const updatePayload = {
          Phone: updated.Phone,
          GlobalContactPhone: updated.GlobalContactPhone,
          PhoneEnrichmentAvailableDate: updated.PhoneEnrichmentAvailableDate
        }
        onPhoneUpdated?.(updatePayload)
        // A lookup that ran and found nobody is not a failure, so it does not get the error
        // toast. It deliberately does NOT start a cooldown either: a contact the provider has
        // nothing on today may have a number next month, and locking the button for weeks over
        // an empty result is worse than the occasional repeat call. The button stays live and
        // this toast is the whole of the feedback (ORB-828).
        if (updated.PhoneFound) {
          showSuccess('Phone enrichment successful', '')
        } else {
          showInfo('No phone number found', 'The lookup ran but returned no number for this contact.')
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      showError('Failed to enrich phone', '')
    } finally {
      if (!abortController.signal.aborted) {
        setIsEnriching(false)
      }
    }
  }

  const handleUpdatePhone = async (e: MouseEvent) => {
    e.stopPropagation()
    if (!hasUpdatedPhone || isUpdating) return

    abortControllerRef.current?.abort()
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsUpdating(true)
    try {
      const resp = context === 'fileImportRow'
            ? await audienceApi.syncPhone(clientCompanyId, workspaceId, campaignId, id, abortController.signal)
        : await leadsApi.syncPhone(workspaceId, campaignId, id, abortController.signal)
      const updated = resp?.data as PhoneEnrichmentResultDto | undefined
      if (updated) {
        const updatePayload = {
          Phone: updated.Phone,
          GlobalContactPhone: updated.GlobalContactPhone,
          PhoneEnrichmentAvailableDate: updated.PhoneEnrichmentAvailableDate
        }
        onPhoneUpdated?.(updatePayload)
        showSuccess('Phone updated', '')
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      showError('Failed to update phone', '')
    } finally {
      if (!abortController.signal.aborted) {
        setIsUpdating(false)
      }
    }
  }

  return (
    <div class="flex items-center gap-2">
      <span class="text-gray-700 dark:text-gray-300">{phone || '—'}</span>

      {/* Enrich button */}
      {canEdit && <div class="relative group/tooltip">
        <button
          ref={enrichButtonRef}
          type="button"
          aria-label="Enrich phone"
          disabled={!canEnrich || isEnriching}
          onClick={handleEnrichPhone}
          class="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {isEnriching ? (
            <Loader2 class="w-3.5 h-3.5 text-primary-600 dark:text-primary-400 animate-spin" />
          ) : (
            <PhoneIcon class="w-3.5 h-3.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400" />
          )}
        </button>
        {!canEnrich && phoneEnrichmentAvailableDate && (
          <div
            class={`absolute right-0 hidden group-hover/tooltip:block min-w-[280px] px-3 py-2 text-xs rounded-md shadow-lg z-50 pointer-events-none bg-gray-800 dark:bg-gray-700 text-gray-200 border border-gray-700 dark:border-gray-600 ${
              tooltipPosition === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
            }`}
          >
            <div class="flex items-start gap-2">
              <Info class="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary-400" />
              <div class="flex-1 leading-relaxed break-words whitespace-normal">
                Phone enrichment available again on {new Date(phoneEnrichmentAvailableDate).toLocaleDateString()}
              </div>
            </div>
          </div>
        )}
      </div>}

      {/* Update indicator */}
      {canEdit && hasUpdatedPhone && (
        <div class="relative group/update flex items-center">
          <div class="w-2 h-2 rounded-full bg-primary-500 cursor-pointer" />
          {/* Invisible hover bridge */}
          <div class="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-20 hidden group-hover/update:block" />
          <div class="absolute top-full left-1/2 -translate-x-1/2 mt-1 hidden group-hover/update:block z-50">
            <div class="w-56 px-3 py-2 text-xs rounded-md shadow-lg bg-gray-800 dark:bg-gray-700 text-gray-200 border border-gray-700 dark:border-gray-600">
              <div class="flex flex-col gap-2">
                <div class="flex items-start gap-2">
                  <Info class="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary-400" />
                  <div>
                    <div class="font-medium">Updated phone available</div>
                    <div class="text-gray-400 mt-1">{globalContactPhone}</div>
                  </div>
                </div>
                <button
                  type="button"
                  disabled={isUpdating}
                  onClick={handleUpdatePhone}
                  class="w-full px-2 py-1.5 text-xs font-medium rounded bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-colors"
                >
                  {isUpdating ? 'Updating...' : 'Update Phone'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
